import React, { useState, useRef, useEffect } from "react";
import VideoPlayer from "../Video/VideoPlayer";
import PlacementTestContent, {
  PlacementTestContentRef,
} from "./PlacementTestContent";
import {
  UserVideoWord,
  VideoActivity,
} from "@shared_types";
import { VideoPlayerHandle } from "../Video/VideoPlayer";
import "../../App.css";
import { useDivSize } from "@/hooks/UseDivSize";
import { LessonHeaderHeightPx } from "@/components/Activities/LessonHeader";
import { isInvalidDictionaryWord } from "@/components/Subtitles/invalidDictionaryWord";
import { getPlatform } from "@/lib/iap";
import ContinueButton from "@/components/Buttons/ContinueButton";

interface PlacementVideoProps {
  videoActivity: VideoActivity;
  isHorizontal: boolean;
  onContinue: () => void;
}

const PlacementVideo: React.FC<PlacementVideoProps> = ({
  videoActivity,
  isHorizontal,
  onContinue,
}) => {
  const platform = getPlatform();
  const [selectedWord, setSelectedWord] = useState({} as UserVideoWord);
  const targetWord = selectedWord?.displayText || "";
  const nativeWord = selectedWord?.definition?.nativeWord || "";
  const videoRef = useRef<VideoPlayerHandle>(null);
  const videoContentRef = useRef<PlacementTestContentRef>(null);
  const [isDisplayHelp, setIsDisplayHelp] = useState(true);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const userVideoWords = videoActivity.userVideoWords;
  const startTime = videoActivity.start;
  const endTime = videoActivity.end;
  const pauseHandleTimeUpdate = useRef(false);
  const { ref: videoPageRef, height: videoPageHeight } = useDivSize();
  const { ref: videoContentDivRef, height: videoContentDivHeight } = useDivSize();

  useEffect(() => {
    if (userVideoWords.length > 0 && !selectedWord.displayText) {
      const firstWord = userVideoWords[0];
      if (!isInvalidDictionaryWord(firstWord.displayText)) {
        handleWordClick(firstWord);
      }
    }
  }, []);

  let bottomPadding = 15;
  if (isHorizontal) {
    bottomPadding = 15;
  }

  let videoContentStyleHeight = "var(--video-content-height)";

  if (isHorizontal) {
    videoContentStyleHeight = "calc(var(--video-content-height) + 20px)";
  }

  const videoPlayerHeight = `${
    videoPageHeight -
    videoContentDivHeight -
    LessonHeaderHeightPx -
    bottomPadding +
    22
  }px`;

  const handleWordClick = async (word: UserVideoWord) => {
    pauseHandleTimeUpdate.current = true;
    setSelectedWord(word);
    setCurrentTime(word.start);
    setIsDisplayHelp(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.seekTo(word.start);
    }
  };

  const handleTimeUpdate = (timeMs: number) => {
    if (!pauseHandleTimeUpdate.current) {
      setCurrentTime(timeMs);
    }
  };

  const handlePlay = () => {
    pauseHandleTimeUpdate.current = false;
    videoContentRef.current?.clearPhraseSelection();
  };

  const handlePhraseSelectStart = () => {
    pauseHandleTimeUpdate.current = true;
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  return (
    <div
      ref={videoPageRef}
      className={`relative flex flex-col items-center w-full max-w-[700px] h-[100svh] z-0 overflow-hidden`}
    >
      <VideoPlayer
        ref={videoRef}
        youtubeId={videoActivity.youtubeId}
        onTimeUpdate={handleTimeUpdate}
        startTime={startTime as number}
        endTime={endTime as number}
        onPlay={handlePlay}
        className={"w-full h-full"}
        isHorizontal={isHorizontal}
        videoPlayerHeight={videoPlayerHeight}
      />
      <div
        ref={videoContentDivRef}
        style={{ minHeight: videoContentStyleHeight, height: "auto" }}
        className={`absolute bottom-10 flex flex-col justify-end flex-grow w-full bg-[#fafafa] max-h-[320px] h-[30%] gap-1 rounded-t-xl`}
      >
        <PlacementTestContent
          ref={videoContentRef}
          currentTime={currentTime as number}
          userVideoWords={userVideoWords}
          onWordClick={handleWordClick}
          onPhraseSelectStart={handlePhraseSelectStart}
          isDisplayHelp={isDisplayHelp}
          targetWord={targetWord}
          nativeWord={nativeWord}
          isHorizontal={isHorizontal}
        />
        <div
          className={`flex justify-center items-center px-4 ${
            platform === "web" ? "bottom-padding-web" : "pb-6"
          }`}
          style={{ pointerEvents: "auto" }}
        >
          <ContinueButton
            onClick={onContinue}
            text="Rate difficulty"
          />
        </div>
      </div>
    </div>
  );
};

export default PlacementVideo; 